/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useMemo } from 'react';
import { gnoment } from 'shared-ui/gnoment';
import isEmpty from 'lodash/isEmpty';
import {
    Alert,
    DividerNew as Divider,
    FlexCol,
    FlexRow,
    Stack,
    Text,
} from 'sarsaparilla';
import { getUnitCardinality } from 'ui-permit/src/utils/utilsNew';
import CostsTable from './CostsTable';
import { PassTypeProps, ReservationProps } from '../props';
import { getSeasonDates } from '../utils';
import { getDuration, validToDate } from '../utils/duration_utils';
import { isDailyPassType } from '../utils/date_time_utils';

function DetailSummary({ passType, reservation }) {
    const passCosts = useMemo(() => {
        const daySegmentGroups = passType?.features?.['day-segment']?.groups || [];
        const daySegmentAttendees = reservation?.features?.['day-segment']?.groups || [];
        const groups = passType?.features?.['attendee-info'] || daySegmentGroups;
        const attendees = reservation?.features?.['attendee-info'] || daySegmentAttendees;
        const nonResFeeQuantity = reservation?.features?.non_resident_fee?.quantity;
        const attendeeInfo = [];
        const costs = [
            { title: `${passType?.name}`, quantity: 'x 1', value: reservation?.price },
        ];
        if (reservation?.fees) {
            costs.push({
                title: `Service Fee`,
                quantity: 'x 1',
                value: reservation?.fees,
            });
        }
        const total = { title: 'Subtotal', value: reservation?.total };
        let allowedToBuy = true;

        let nonResFee = null;
        if (nonResFeeQuantity) {
            nonResFee = (
                <span className="parkpass-reservation-summary-non-res-fee-info">
                    {`${nonResFeeQuantity} non-U.S. ${getUnitCardinality(nonResFeeQuantity, 'resident', 'residents')}`}
                </span>
            );
        }

        Object.entries(groups).forEach(([key, group]) => {
            const count = attendees[key];

            if (!count) {
                return;
            }

            if (group?.attendee_max > -1) {
                if (group?.price_per_attendee && count > group?.attendee_max) {
                    allowedToBuy = false;
                }
            }

            attendeeInfo.push(
                <span key={key} className="parkpass-reservation-summary-attendee-info">
                    {`${count} ${getUnitCardinality(count, key.replace(/s$|ren$/, ''), key)} `}
                </span>
            );

            let cost;

            cost = Math.max(group.price_min, count * group.price_per_attendee);
            if (group.price_max > 0) {
                cost = Math.min(group.price_max, cost);
            }

            if (cost) {
                costs.push({
                    title: passType?.name,
                    quantity: `x 1`,
                    description: passType?.description,
                    value: cost,
                });

                total.value += cost;
            }
        });

        return { allowedToBuy, attendeeInfo, nonResFee, costs, total };
    }, [
        passType?.description,
        passType?.features,
        passType?.name,
        reservation?.features,
        reservation?.fees,
        reservation?.price,
        reservation?.total,
    ]);

    const values = useMemo(() => {
        const totalCost = { title: 'Total Cost', value: 0 };
        const { total: totalPassCost } = passCosts;
        totalCost.value += totalPassCost?.value || 0;

        const isSeasonal = ['season', 'seasonal'].includes(passType.duration);
        const DATE_FORMAT = 'ddd, ll';
        const [seasonStart] = getSeasonDates(passType.periods);
        let [, seasonEnd] = getSeasonDates(passType.periods);
        const startDateMoment = gnoment(
            isSeasonal ? seasonStart : reservation?.pass_info?.features['start-date']
        );

        let startDate = startDateMoment.isValid()
            ? startDateMoment.format(DATE_FORMAT)
            : 'Select Start date';
        let endDate = startDateMoment.isValid()
            ? validToDate(startDateMoment, passType).format(DATE_FORMAT)
            : 'Select Start date';
        // Some durations are flexible/user-customizable, so we have to calculate them instead of using durations.
        const duration = getDuration(passType.duration);
        const canModifyEndDate =
            passType.features['end-date'] ||
            passType.features['day-segment'] ||
            passType.duration === 'segment';
        if (canModifyEndDate) {
            const end = reservation.pass_info.features['end-date'];
            duration.days = gnoment(end).add(2, 'days').diff(gnoment(), 'days');
        }
        if (isSeasonal) {
            // For seasonal pricing, we have to get the end date ourselves.
            duration.days = gnoment(seasonEnd).add(2, 'days').diff(gnoment(), 'days');

            endDate = gnoment(seasonEnd).format(DATE_FORMAT);
        }

        if (!gnoment(seasonEnd).isValid()) {
            const [year, month, day] = seasonEnd?.split('-') ?? [
                undefined,
                undefined,
                undefined,
            ];
            const adjustedYear = parseInt(year, 10) + 1;
            seasonEnd = `${adjustedYear}-${month}-${day}`;
            endDate = gnoment(seasonEnd).format(DATE_FORMAT);
        }

        const isLimitedToSeason = passType.duration.indexOf('limit-to-season') !== -1;

        if (isLimitedToSeason) {
            const days = duration.days - 1;
            const endValue = gnoment(startDateMoment).add(days, 'd');
            const isAfterSeasonEnd = endValue.isAfter(seasonEnd, 'day');
            endDate = endValue.format(DATE_FORMAT);

            if (isAfterSeasonEnd) {
                endDate = gnoment(seasonEnd).format(DATE_FORMAT);
            }
        }

        const isEmptyDailyPassDate =
            isDailyPassType(passType?.duration) &&
            isEmpty(reservation?.pass_info?.features['start-date']);

        if (isEmptyDailyPassDate) {
            startDate = 'Select Start date';
            endDate = 'Select Start date';
        }

        return { startDate, endDate, totalCost };
    }, [passCosts, passType, reservation.pass_info.features]);

    const hasEmptyDates = useMemo(() => {
        return (
            values?.startDate === 'Select Start date' ||
            values?.endDate === 'Select Start date'
        );
    }, [values?.endDate, values?.startDate]);

    if (!passType) {
        return null;
    }

    return (
        <Stack>
            <FlexRow>
                <FlexCol xs={6} md={12} xl={6}>
                    <FlexRow xs={12}>
                        <FlexCol>
                            <Text color="gray" size="sm">
                                Start Date
                            </Text>
                        </FlexCol>
                        <FlexCol xs={12}>
                            <Text fontWeight="semibold">{values?.startDate}</Text>
                        </FlexCol>
                    </FlexRow>
                </FlexCol>

                <FlexCol xs={6} md={12} xl={6}>
                    <FlexRow>
                        <FlexCol xs={12}>
                            <Text color="gray" size="sm">
                                Valid Through
                            </Text>
                        </FlexCol>
                        <FlexCol xs={12}>
                            <Text fontWeight="semibold">{values?.endDate}</Text>
                        </FlexCol>
                    </FlexRow>
                </FlexCol>
            </FlexRow>
            {!hasEmptyDates && (
                <>
                    <Divider />
                    {passCosts?.allowedToBuy ? (
                        <div>
                            <CostsTable
                                lines={passCosts?.costs}
                                total={passCosts?.total}
                                ariaLabel={`Pass Summary: 1 ${passType.name}, The start date is ${values?.startDate} and is valid through ${values?.endDate}`}
                                attendeeInfo={passCosts?.attendeeInfo}
                                nonResFee={passCosts?.nonResFee}
                            />
                        </div>
                    ) : (
                        <Alert type="error">
                            Your selections are outside the bounds for this pass type.
                            Please make a different selection.
                        </Alert>
                    )}
                    <Divider />

                    <CostsTable
                        lines={[]}
                        total={values?.totalCost}
                        showNoLinesText={!reservation}
                    />
                </>
            )}
        </Stack>
    );
}

DetailSummary.propTypes = {
    passType: PassTypeProps,
    reservation: ReservationProps,
};

export default DetailSummary;
